import type { ItemFile } from '../../../core/item';
import type { 
  UploadService, 
  UploadItemFile, 
  UploadOptions, 
  UploadCancelFunction,
  UploadProgress 
} from '../types';
import { useContainer } from '../../../hooks/useContainer';

export class ItemUploadService implements UploadService {
  validateItem = (item: UploadItemFile): { isValid: boolean; error?: string } => {
    if (!item.file) {
      return { isValid: false, error: 'No file selected' };
    }

    if (!item.name.trim()) {
      return { isValid: false, error: 'File name is required' };
    }

    // Additional validation can be added here
    const maxFileSize = 10000 * 1024 * 1024; //   10GB
    if (item.file.size > maxFileSize) {
      return { isValid: false, error: 'File size exceeds 10GBlimit' };
    }

    return { isValid: true };
  };

  uploadItem = async (
    item: UploadItemFile,
    options?: UploadOptions
  ): Promise<UploadCancelFunction | null> => {
    // Validate item first
    const validation = this.validateItem(item);
    if (!validation.isValid) {
      options?.onError?.(validation.error || 'Invalid item');
      return null;
    }

    // Get container client
    const { client } = useContainer.getState();
    if (!client) {
      const error = 'Upload nicht möglich: Bitte geben Sie zuerst einen gültigen SAS-Link ein';
      options?.onError?.(error);
      return null;
    }

    try {
      // Convert to ItemFile format expected by the existing client
      const itemFile: ItemFile = {
        file: item.file!,
        name: item.name,
        data: item.owner ? { owner: item.owner } : {},
      };

      // Create progress callback
      const progressCallback = (size: number, loaded: number) => {

        const progress: UploadProgress = {
          loaded,
          total: size,
          percentage: Math.round((loaded / size) * 100),
        };
        
        options?.onProgress?.(progress);

        // Check if upload is complete
        if (loaded === size) {
          options?.onSuccess?.(item);
        }
      };

      progressCallback(itemFile.file.size, 0); // Initial progress

      // Start upload
      const cancelFunction = client.uploadItem(itemFile, progressCallback);

      if (!cancelFunction) {
        const error = 'Upload konnte nicht gestartet werden';
        options?.onError?.(error);
        return null;
      }

      return cancelFunction;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      options?.onError?.(errorMessage);
      return null;
    }
  };
}

// Singleton instance
export const uploadService = new ItemUploadService();